// Common Expressions pillar — a calm browser for figurative phrases whose
// literal words mislead (e.g. `non vedo l'ora` = "I can't wait", not "I don't
// see the hour"). Each card stacks three layers: the expression, the literal
// "sounds like" trap (muted), and the real meaning (bold), plus a worked example
// with audio. Filterable by anchoring verb, searchable across Italian + English.
//
// STATIC view — reads straight from ../data/expressions.js. No PocketBase, no
// pins/notes, no auth. Same visual language as the Vocabulary browser.
import { EXPRESSIONS, EXPRESSION_CATEGORIES } from "../data/expressions.js";
import { escapeHtml, highlightMatch } from "../ui/helpers.js";
import { speakButtonHTML } from "../lib/audio.js";

const CAT_LABEL = Object.fromEntries(EXPRESSION_CATEGORIES.map((c) => [c.id, c.label]));
const CAT_EMOJI = Object.fromEntries(EXPRESSION_CATEGORIES.map((c) => [c.id, c.emoji]));

export async function expressionsView(app, params = {}) {
  const total = EXPRESSIONS.length;

  app.innerHTML = `
    <section class="fade-in">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800">Common Expressions</h2>
        <p class="text-sage-600 text-sm mt-1">${total} everyday phrases that mean something different from their literal words. Each one shows what it <em>sounds like</em>, what it <em>really means</em>, and how it's used.</p>
      </div>

      <div class="mb-5 rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex items-start gap-3">
        <span class="text-2xl leading-none" aria-hidden="true">💡</span>
        <p class="text-sm text-amber-900">
          <b>Why these are tricky:</b> read word-for-word, <span class="font-mono text-amber-800">non vedo l'ora</span> looks like
          <span class="italic">"I don't see the hour"</span> — but it actually means <b>"I can't wait"</b>. The trick is to learn the
          whole phrase as one unit, not the words inside it.
        </p>
      </div>

      <div class="relative mb-3">
        <div class="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 pointer-events-none">🔍</div>
        <input id="expr-search" type="text" placeholder="Try 'wait', 'luck', or 'fare'…"
          class="w-full pl-11 pr-4 py-3 rounded-xl border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-base" />
      </div>

      <div class="flex flex-wrap items-center gap-1.5 mb-4" id="cat-chips">
        <button class="cat-chip" data-cat="">All</button>
        ${EXPRESSION_CATEGORIES.map(
          (c) => `<button class="cat-chip" data-cat="${c.id}">${c.emoji} ${escapeHtml(c.label)}</button>`
        ).join("")}
      </div>

      <p id="expr-count" class="text-xs text-sage-500 mb-3"></p>
      <div id="expr-list"></div>
    </section>`;

  const input = app.querySelector("#expr-search");
  const list = app.querySelector("#expr-list");
  const count = app.querySelector("#expr-count");
  let cat = params.cat && CAT_LABEL[params.cat] ? params.cat : "";

  function paintChips() {
    app.querySelectorAll(".cat-chip").forEach((b) => {
      const on = b.dataset.cat === cat;
      b.className = `cat-chip px-2.5 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
  }

  function render() {
    const q = input.value.trim().toLowerCase();
    const results = EXPRESSIONS.filter((x) => {
      if (cat && x.category !== cat) return false;
      if (!q) return true;
      return (
        x.spanish.toLowerCase().includes(q) ||
        x.meaning.toLowerCase().includes(q) ||
        x.literal.toLowerCase().includes(q) ||
        (x.example_es || "").toLowerCase().includes(q)
      );
    });

    count.innerHTML = q
      ? `<b>${results.length}</b> match${results.length === 1 ? "" : "es"} for "<span class='text-sage-700'>${escapeHtml(q)}</span>"`
      : `${results.length} expression${results.length === 1 ? "" : "s"}${cat ? " · " + escapeHtml(CAT_LABEL[cat]) : ""}`;

    if (!results.length) {
      list.innerHTML = `<div class="text-center py-10 text-sage-500">
        <p class="text-2xl mb-2">🤔</p><p>No expressions found${q ? ` for "${escapeHtml(q)}"` : ""}.</p>
        <p class="text-xs mt-2 text-sage-400">Try the English meaning, drop accents, or clear the filters.</p></div>`;
      return;
    }

    // Group by anchoring verb so the family resemblance is visible.
    const order = EXPRESSION_CATEGORIES.map((c) => c.id);
    const groups = new Map();
    for (const x of results) {
      if (!groups.has(x.category)) groups.set(x.category, []);
      groups.get(x.category).push(x);
    }
    const sorted = [...groups.keys()].sort((a, b) => order.indexOf(a) - order.indexOf(b));

    list.innerHTML = sorted
      .map((c) => {
        const items = groups.get(c);
        return `
          <div class="mb-6">
            <h3 class="flex items-center gap-2 text-sm font-semibold text-sage-700 mb-2 sticky top-16 bg-white/80 backdrop-blur py-1 z-10">
              <span class="text-base">${CAT_EMOJI[c] || "🗂️"}</span>${escapeHtml(CAT_LABEL[c] || c)}
              <span class="text-xs font-normal text-sage-400">${items.length}</span>
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              ${items.map((x) => cardHTML(x, q)).join("")}
            </div>
          </div>`;
      })
      .join("");

    list.querySelectorAll("[data-expr-rank]").forEach((el) => {
      el.addEventListener("click", () => {
        const x = EXPRESSIONS.find((e) => String(e.rank) === el.dataset.exprRank);
        if (x) showDetail(x);
      });
    });
  }

  function cardHTML(x, q) {
    return `
      <button data-expr-rank="${x.rank}"
        class="group bg-white border border-sage-100 hover:border-sage-300 hover:shadow-sm rounded-xl p-3.5 text-left transition flex items-start gap-3">
        <span class="text-2xl leading-none mt-0.5 transition-transform group-hover:scale-110" aria-hidden="true">${x.emoji || "•"}</span>
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-sage-800 truncate">${highlightMatch(x.spanish, q)}</p>
          <p class="text-xs text-sage-400 italic truncate">sounds like “${highlightMatch(x.literal, q)}”</p>
          <p class="text-sm text-sage-600 mt-1 truncate">→ <b class="text-sage-700">${highlightMatch(x.meaning, q)}</b></p>
        </div>
      </button>`;
  }

  input.addEventListener("input", render);
  app.querySelectorAll(".cat-chip").forEach((b) =>
    b.addEventListener("click", () => {
      cat = b.dataset.cat;
      paintChips();
      render();
    })
  );

  paintChips();
  render();
}

// ---- Expression detail modal --------------------------------------------

function onModalKeydown(e) {
  if (e.key === "Escape") closeDetail();
}

function closeDetail() {
  const existing = document.getElementById("expr-modal");
  if (existing) existing.remove();
  document.removeEventListener("keydown", onModalKeydown);
}

function showDetail(x) {
  closeDetail();

  const overlay = document.createElement("div");
  overlay.id = "expr-modal";
  overlay.className =
    "fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 bg-sage-900/30 backdrop-blur-sm overflow-y-auto fade-in";

  const card = document.createElement("div");
  card.className =
    "bg-white border border-sage-200 rounded-2xl p-5 sm:p-6 shadow-xl w-full max-w-lg my-8 relative";
  card.innerHTML = `
    <button data-close aria-label="Close" class="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-sage-100 text-sage-500 flex items-center justify-center text-lg">✕</button>
    <div class="pr-10 flex items-start gap-4">
      <span class="text-5xl leading-none" aria-hidden="true">${x.emoji || "•"}</span>
      <div class="min-w-0">
        <h3 class="text-2xl font-bold text-sage-800 flex items-center gap-2">${escapeHtml(x.spanish)}${speakButtonHTML(x.spanish, { size: "lg" })}</h3>
        <span class="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border bg-sage-50 text-sage-600 border-sage-100">${CAT_EMOJI[x.category] || ""} ${escapeHtml(CAT_LABEL[x.category] || x.category)}</span>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      <div class="rounded-xl border border-rose-100 bg-rose-50/50 p-3">
        <p class="text-[10px] uppercase tracking-wide text-rose-400 font-semibold mb-1">Sounds like</p>
        <p class="text-sage-700 italic">“${escapeHtml(x.literal)}”</p>
      </div>
      <div class="rounded-xl border border-sage-200 bg-sage-50/60 p-3">
        <p class="text-[10px] uppercase tracking-wide text-sage-400 font-semibold mb-1">Really means</p>
        <p class="text-sage-800 font-semibold">${escapeHtml(x.meaning)}</p>
      </div>
    </div>

    ${
      x.example_es
        ? `<div class="mt-3 rounded-xl border border-sand-200 bg-sand-50/60 p-4">
            <p class="text-sage-800 flex items-start gap-1.5">${escapeHtml(x.example_es)}${speakButtonHTML(x.example_es)}</p>
            <p class="text-sm text-sage-500 mt-1 italic">${escapeHtml(x.example_en || "")}</p>
          </div>`
        : ""
    }`;

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  overlay.addEventListener("mousedown", (e) => {
    if (e.target === overlay) closeDetail();
  });
  card.querySelector("[data-close]").addEventListener("click", closeDetail);
  document.addEventListener("keydown", onModalKeydown);
}
